import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cartService } from '../services/cartService';
import { wishlistService } from '../services/wishlistService';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isWide, setIsWide] = useState(window.innerWidth > 768);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [cartCount, setCartCount] = useState(cartService.getCount(undefined));
  const [wishlistCount, setWishlistCount] = useState(wishlistService.getCount(undefined));
  const menuRef = useRef<HTMLDivElement>(null);

  // Refresh counts on every route change or auth change
  useEffect(() => {
    setCartCount(cartService.getCount(user?.id));
    setWishlistCount(wishlistService.getCount(user?.id));
  }, [location, user]);

  // Also refresh counts whenever localStorage changes (e.g., add to cart/wishlist from any page)
  useEffect(() => {
    const handleStorage = () => {
      setCartCount(cartService.getCount(user?.id));
      setWishlistCount(wishlistService.getCount(user?.id));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user]);

  React.useEffect(() => {
    const handleResize = () => setIsWide(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 h-[60px] flex items-center px-4 md:px-10">
        <div className="flex items-center justify-between w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/assets/images/sasta-fashions-logo.png"
              alt="Sasta Fashions"
              className="h-10 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
              }}
            />
            <div className="hidden w-9 h-9 bg-[#DAA520] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">SF</span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          {isWide && (
            <div className="flex items-center space-x-8 ml-8">
              <Link to="/" className="text-[14px] font-medium text-[#1A1A1A] hover:text-[#DAA520]">
                Home
              </Link>
              <Link to="/women" className="text-[14px] font-medium text-[#1A1A1A] hover:text-[#DAA520]">
                Women
              </Link>
              <Link to="/men" className="text-[14px] font-medium text-[#1A1A1A] hover:text-[#DAA520]">
                Men
              </Link>
              <Link to="/kids" className="text-[14px] font-medium text-[#1A1A1A] hover:text-[#DAA520]">
                Children
              </Link>
            </div>
          )}

          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Search */}
            {isWide ? (
              <div className="w-[180px] h-[36px] relative">
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const searchValue = formData.get('search') as string;
                    if (searchValue && searchValue.trim()) {
                      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
                    }
                  }}
                  className="h-full"
                >
                  <input
                    type="text"
                    name="search"
                    placeholder="Search"
                    className="w-full h-full px-3 text-[13px] border border-gray-300 rounded focus:outline-none focus:border-[#DAA520] bg-white"
                  />
                  <button 
                    type="submit"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </form>
              </div>
            ) : (
              <button onClick={() => navigate('/search')} className="p-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}

            {/* Profile / Auth */}
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  className="flex items-center space-x-1 p-2"
                >
                  <svg className="w-4 h-4 text-[#DAA520]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {isWide && (
                    <span className="text-[13px] font-semibold">
                      {user.name.split(' ')[0]}
                    </span>
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-100 z-50 py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{user.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { navigate('/wallet'); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
                    >
                      💰 My Wallet
                    </button>
                    <button
                      onClick={() => { navigate('/orders'); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
                    >
                      My Orders
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-[13px] text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : isWide ? (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/login')}
                  className="text-[13px] font-medium text-[#1A1A1A] hover:text-[#DAA520] px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="text-[13px] font-medium text-white bg-[#DAA520] hover:bg-[#c5941c] px-3 py-1.5 rounded-lg transition-colors"
                >
                  Sign Up
                </button>
              </div>
            ) : (
              <button onClick={() => navigate('/login')} className="p-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            )}

            {/* Wallet */}
            {user && (
              <button onClick={() => navigate('/wallet')} className="p-2 relative" title="My Wallet">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </button>
            )}

            {/* Wishlist */}
            <button onClick={() => navigate('/wishlist')} className="p-2 relative">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {wishlistCount > 99 ? '99+' : wishlistCount}
                </span>
              )}
            </button>

            {/* Cart */}
            <button onClick={() => navigate('/cart')} className="p-2 relative">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#E91E63] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Festive Sale Banner */}
      <div className="bg-gradient-to-r from-[#E91E63] via-[#DAA520] to-[#E91E63] text-white overflow-hidden">
        <div className="flex items-center py-2 px-4 gap-6 text-center justify-center flex-wrap">
          <span className="text-sm font-bold tracking-wide">🎉 FESTIVE OFFER SALE</span>
          <span className="hidden sm:inline text-sm font-medium">Huge Discounts on Kids Fashion</span>
          <span className="text-sm font-medium">All Prices Include GST</span>
          <span className="hidden sm:inline text-sm font-medium">⏰ Limited Time</span>
          <button
            onClick={() => navigate('/women')}
            className="bg-white text-[#E91E63] text-xs font-bold px-3 py-1 rounded-full hover:bg-yellow-50 transition-colors whitespace-nowrap"
          >
            Shop Now →
          </button>
        </div>
      </div>

      <main>{children}</main>

      {/* Footer - Exact copy of Flutter footer */}
      <footer className="mt-12">
        {/* Main footer section */}
        <div className="bg-[#2D2D2D] text-white py-12 px-4 md:px-20">
          {isWide ? (
            <div className="grid grid-cols-4 gap-8 max-w-7xl mx-auto">
              {/* About */}
              <div className="space-y-4">
                <h3 className="font-bold text-base">About</h3>
                <p className="text-[13px] text-white/60 leading-relaxed">
                  Sasta Fashions is a brand owned and operated by SASTA DEAL PRIVATE LIMITED. We are an e-commerce platform offering fashion products and lifestyle essentials, committed to providing quality products, affordable prices, and a seamless shopping experience across India.
                </p>
              </div>

              {/* Categories */}
              <div className="space-y-4">
                <h3 className="font-bold text-base">Categories</h3>
                <div className="space-y-2">
                  <Link to="/women" className="block text-[13px] text-white/60 hover:text-white">
                    Women
                  </Link>
                  <Link to="/men" className="block text-[13px] text-white/60 hover:text-white">
                    Men
                  </Link>
                  <Link to="/kids" className="block text-[13px] text-white/60 hover:text-white">
                    Children
                  </Link>
                </div>
              </div>

              {/* Pages */}
              <div className="space-y-4">
                <h3 className="font-bold text-base">Pages</h3>
                <div className="space-y-2">
                  <Link to="/about" className="block text-[13px] text-white/60 hover:text-white">
                    About Us
                  </Link>
                  <Link to="/policy/privacy" className="block text-[13px] text-white/60 hover:text-white">
                    Privacy Policy
                  </Link>
                  <Link to="/policy/refund" className="block text-[13px] text-white/60 hover:text-white">
                    Refund Policy
                  </Link>
                  <Link to="/policy/cancellation" className="block text-[13px] text-white/60 hover:text-white">
                    Cancellation Policy
                  </Link>
                  <Link to="/policy/terms" className="block text-[13px] text-white/60 hover:text-white">
                    Terms and Conditions
                  </Link>
                  <Link to="/policy/return" className="block text-[13px] text-white/60 hover:text-white">
                    Return & Refund Policy
                  </Link>
                  <Link to="/policy/shipping" className="block text-[13px] text-white/60 hover:text-white">
                    Shipping Policy
                  </Link>
                </div>
              </div>

              {/* Contact Us */}
              <div className="space-y-4">
                <h3 className="font-bold text-base">Contact Us</h3>
                <p className="text-[13px] text-white/60 leading-relaxed">
                  Address: Floor No. Gr - Fr, Building No. 114/K, Raja S.C. Mullick Road, Near Naktala, Garia, Kolkata, West Bengal - 700047
                </p>
                <p className="text-[13px] text-white/60">
                  Email : <a href="mailto:sastadealpvt@gmail.com" className="text-white/80 hover:text-white underline">sastadealpvt@gmail.com</a>
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* About */}
              <div className="space-y-3">
                <h3 className="font-bold text-base">About</h3>
                <p className="text-[13px] text-white/60 leading-relaxed">
                  Sasta Fashions is a brand owned and operated by SASTA DEAL PRIVATE LIMITED. We are an e-commerce platform offering fashion products and lifestyle essentials, committed to providing quality products, affordable prices, and a seamless shopping experience across India.
                </p>
              </div>

              {/* Categories */}
              <div className="space-y-3">
                <h3 className="font-bold text-base">Categories</h3>
                <div className="space-y-2">
                  <Link to="/women" className="block text-[13px] text-white/60 hover:text-white">
                    Women
                  </Link>
                  <Link to="/men" className="block text-[13px] text-white/60 hover:text-white">
                    Men
                  </Link>
                  <Link to="/kids" className="block text-[13px] text-white/60 hover:text-white">
                    Children
                  </Link>
                </div>
              </div>

              {/* Pages */}
              <div className="space-y-3">
                <h3 className="font-bold text-base">Pages</h3>
                <div className="space-y-2">
                  <Link to="/about" className="block text-[13px] text-white/60 hover:text-white">
                    About Us
                  </Link>
                  <Link to="/policy/privacy" className="block text-[13px] text-white/60 hover:text-white">
                    Privacy Policy
                  </Link>
                  <Link to="/policy/refund" className="block text-[13px] text-white/60 hover:text-white">
                    Refund Policy
                  </Link>
                  <Link to="/policy/cancellation" className="block text-[13px] text-white/60 hover:text-white">
                    Cancellation Policy
                  </Link>
                  <Link to="/policy/terms" className="block text-[13px] text-white/60 hover:text-white">
                    Terms and Conditions
                  </Link>
                  <Link to="/policy/return" className="block text-[13px] text-white/60 hover:text-white">
                    Return & Refund Policy
                  </Link>
                  <Link to="/policy/shipping" className="block text-[13px] text-white/60 hover:text-white">
                    Shipping Policy
                  </Link>
                </div>
              </div>

              {/* Contact Us */}
              <div className="space-y-3">
                <h3 className="font-bold text-base">Contact Us</h3>
                <p className="text-[13px] text-white/60 leading-relaxed">
                  Floor No. Gr - Fr, Building No. 114/K, Raja S.C. Mullick Road, Near Naktala, Garia, Kolkata, West Bengal - 700047
                </p>
                <p className="text-[13px] text-white/60">
                  Email : <a href="mailto:sastadealpvt@gmail.com" className="text-white/80 hover:text-white underline">sastadealpvt@gmail.com</a>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Copyright bar */}
        <div className="bg-[#222222] py-4 px-4 md:px-20">
          <p className="text-[11px] text-white/40 text-center">
            © 2026 SASTA DEAL PRIVATE LIMITED. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;